import React, { useRef, useState } from 'react';
import { translate } from 'react-jhipster';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { POApprovalCard } from './POApprovalCard';
import { PendingAmountTransferredCard } from './PendingAmountTransferredCard';

interface SummaryStats {
  totalSoldTodayLocal: number;
  totalSoldTodayInternational: number;
  otherCurrencies: Array<{ code: string; amount: string }>;
  quotationCreatedCountLocal: number;
  quotationCreatedCountInternational: number;
  quotationSoldCountLocal: number;
  quotationSoldCountInternational: number;
  quotationPurchaseAndTransferCountLocal: number;
  quotationPurchaseAndTransferCountInternational: number;
  quotationKeepCountLocal: number;
  quotationKeepCountInternational: number;
  lang: string;
}

interface Props {
  summaryStats: SummaryStats;
  quotationPOApprovalToday: number;
  pendingAmountTransferredCount: number;
  isTransferChecker: boolean;
  onPoTablePendingApproval: () => void;
  onNavigate: (path: string) => void;
}

const n = (v: number) => (v > 0 ? v : 0);
const CURRENCY = 'KWD';

const cardSx = {
  width: '100%',
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)',
};

const cardContentSx = { p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } };

function SectionLabel({ color, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="body2" fontWeight={500} color="text.secondary">{children}</Typography>
    </Box>
  );
}

function AmountRow({ amount }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
      <Typography component="span" sx={{ fontSize: { xs: '28px', sm: '34px' }, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: 'text.primary' }}>
        {amount}
      </Typography>
      <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
        {CURRENCY}{' '}
        <Typography component="span" variant="body2" fontWeight={400} color="text.secondary">
          • {translate('home.topInfo.todaysSummary.totalSales.titleWithoutCurrency')}
        </Typography>
        &nbsp;
        <Tooltip title={`${translate('home.topInfo.todaysSummary.totalSales.tooltip')}`}>
          <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer', color: 'text.disabled' }} />
        </Tooltip>
      </Typography>
    </Box>
  );
}

function StatItem({ stat, borderBottom, pr, pl }: { stat: any; borderBottom: boolean; pr?: number; pl?: number }) {
  return (
    <Box sx={{ flex: 1, py: 1.5, borderBottom: borderBottom ? '1px solid' : 'none', borderColor: 'divider', pr, pl }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ display: 'flex', flexShrink: 0, color: 'text.disabled' }}>{stat.icon}</Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
          {stat.label}
        </Typography>
      </Box>
      <Typography variant="body2">
        <Box component="span" fontSize="1.25rem" fontWeight={700} mx={0.5}>{stat.value}</Box>
      </Typography>
    </Box>
  );
}

function StatGrid({ left, right }) {
  return (
    <Box sx={{ display: 'flex', pt: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
        {left.map((stat, i) => (
          <StatItem key={stat.label} stat={stat} borderBottom={i < left.length - 1} pr={1.5} />
        ))}
      </Box>
      <Box sx={{ width: '1px', bgcolor: 'divider', flexShrink: 0 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
        {right.map((stat, i) => (
          <StatItem key={stat.label} stat={stat} borderBottom={i < right.length - 1} pl={1.5} />
        ))}
      </Box>
    </Box>
  );
}

function OtherCurrencyStrip({ currencies, isRtl }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const onMouseDown = (e) => {
    const el = scrollRef.current;
    drag.current = { active: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
    el.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    e.preventDefault();
    const el = scrollRef.current;
    el.scrollLeft = drag.current.scrollLeft - (e.pageX - el.offsetLeft - drag.current.startX);
  };
  const onMouseUp = () => {
    drag.current.active = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (isRtl) {
      const pos = Math.abs(el.scrollLeft);
      setCanScrollLeft(pos < max - 1);
      setCanScrollRight(pos > 1);
    } else {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft < max - 1);
    }
  };

  if (!currencies?.length) {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {translate('home.topInfo.todaysSummary.totalSalesOtherCurrenciesN/A')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', mt: 1, mb: 2, overflow: 'hidden' }}>
      {canScrollLeft && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 32, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(to right, #fff, transparent)' }} />}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, cursor: 'grab', userSelect: 'none', pb: 0.25 }}
      >
        {currencies.map(({ code, amount }) => (
          <Box key={code} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0, px: 1.25, height: 28, borderRadius: '20px', border: '1px solid #e8e8e8', bgcolor: '#f5f5f5', pointerEvents: 'none' }}>
            <Typography component="span" variant="body2" fontWeight={700} color="text.primary">{code}</Typography>
            <Typography component="span" variant="body2" color="text.secondary">{amount}</Typography>
          </Box>
        ))}
      </Box>
      {canScrollRight && <Box sx={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 32, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(to left, #fff, transparent)' }} />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
        {isRtl ? <ArrowBackIcon sx={{ fontSize: 13, color: 'text.secondary' }} /> : <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.secondary' }} />}
        <Typography variant="caption" color="text.secondary">
          {translate('home.topInfo.todaysSummary.totalSalesOtherCurrenciesScrollHint')}
        </Typography>
      </Box>
    </Box>
  );
}

export const AlshmokHStaffDashboard = ({
  summaryStats,
  quotationPOApprovalToday,
  pendingAmountTransferredCount,
  isTransferChecker,
  onPoTablePendingApproval,
  onNavigate,
}: Props) => {
  const { lang, otherCurrencies, totalSoldTodayLocal, totalSoldTodayInternational } = summaryStats;
  const isRtl = lang === 'ar-ly';

  const poAndTransferLabel = translate('home.topInfo.todaysSummary.poAndTransfer');

  const localStats = {
    left: [
      { icon: <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: translate('home.topInfo.todaysSummary.quotations'), value: n(summaryStats.quotationCreatedCountLocal) },
      { icon: <CheckRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: translate('home.topInfo.todaysSummary.soldOrders'), value: n(summaryStats.quotationSoldCountLocal) },
    ],
    right: [
      { icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: poAndTransferLabel, value: n(summaryStats.quotationPurchaseAndTransferCountLocal) },
      { icon: <PendingActionsOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: translate('home.topInfo.todaysSummary.keptOrders'), value: n(summaryStats.quotationKeepCountLocal) },
    ],
  };

  const intlStats = {
    left: [
      { icon: <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: translate('home.topInfo.todaysSummary.quotations'), value: n(summaryStats.quotationCreatedCountInternational) },
      { icon: <CheckRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: translate('home.topInfo.todaysSummary.soldOrders'), value: n(summaryStats.quotationSoldCountInternational) },
    ],
    right: [
      { icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: poAndTransferLabel, value: n(summaryStats.quotationPurchaseAndTransferCountInternational) },
      { icon: <PendingActionsOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, label: translate('home.topInfo.todaysSummary.keptOrders'), value: n(summaryStats.quotationKeepCountInternational) },
    ],
  };

  return (
    <Grid container spacing={2} className="chart-space" alignItems="stretch">

      {/* Column 1: PO Approval + (conditional) Pending Amount Transferred */}
      <Grid item xs={12} lg={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <POApprovalCard
            count={quotationPOApprovalToday}
            onClick={onPoTablePendingApproval}
            onNavigate={onNavigate}
          />
          {isTransferChecker && (
            <PendingAmountTransferredCard
              count={pendingAmountTransferredCount}
              onNavigate={onNavigate}
            />
          )}
        </Box>
      </Grid>

      {/* Column 2: Local Summary — same height as International */}
      <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
        <Card elevation={0} sx={{ ...cardSx, width: '100%' }}>
          <CardContent sx={cardContentSx}>
            <Typography variant="subtitle1" fontWeight={600} mb={2} letterSpacing="-0.01em" component="div">
              {translate('home.topInfo.todaysSummary.title')} ({translate('home.topInfo.todaysSummary.local')})
            </Typography>
            <SectionLabel color="#22c55e">{translate('home.topInfo.todaysSummary.local')}</SectionLabel>
            <AmountRow amount={totalSoldTodayLocal > 0 ? totalSoldTodayLocal.toLocaleString() : '0'} />
            <Divider />
            <StatGrid left={localStats.left} right={localStats.right} />
          </CardContent>
        </Card>
      </Grid>

      {/* Column 3: International Summary — same height as Local */}
      <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
        <Card elevation={0} sx={{ ...cardSx, width: '100%' }}>
          <CardContent sx={cardContentSx}>
            <Typography variant="subtitle1" fontWeight={600} mb={2} letterSpacing="-0.01em" component="div">
              {translate('home.topInfo.todaysSummary.title')} ({translate('home.topInfo.todaysSummary.international')})
            </Typography>
            <SectionLabel color="#3b82f6">{translate('home.topInfo.todaysSummary.international')}</SectionLabel>
            <AmountRow amount={totalSoldTodayInternational > 0 ? totalSoldTodayInternational.toLocaleString() : '0'} />
            <OtherCurrencyStrip currencies={otherCurrencies} isRtl={isRtl} />
            <Divider />
            <StatGrid left={intlStats.left} right={intlStats.right} />
          </CardContent>
        </Card>
      </Grid>

    </Grid>
  );
};

export default AlshmokHStaffDashboard;
